import { get, post } from "./lewooApi";

const GHOST_THRESHOLD_H = 0.02;
const LATE_HOUR = 8;
const LATE_MIN = 30;

export async function fetchEmployee(empId) {
  if (!empId) return null;
  try {
    const res = await get(`/api/employees/${empId}`);
    return res?.data || null;
  } catch {
    return null;
  }
}

export async function fetchAllEmployees() {
  try {
    const res = await get("/api/employees?limit=500");
    return res?.data || [];
  } catch {
    return [];
  }
}

export async function fetchAttendance(employeeId, dateFrom, dateTo) {
  if (!employeeId) return [];
  try {
    const res = await get(`/api/attendance?employee_id=${employeeId}&date_from=${dateFrom}&date_to=${dateTo}`);
    return res?.data || [];
  } catch {
    return [];
  }
}

export async function fetchTodaysAttendance(employeeId) {
  if (!employeeId) return [];
  try {
    const res = await get(`/api/attendance?employee_id=${employeeId}`);
    return res?.data || [];
  } catch {
    return [];
  }
}

export async function fetchLeaves(employeeId) {
  if (!employeeId) return [];
  try {
    const res = await get(`/api/leave-requests?employee_id=${employeeId}`);
    return res?.data || [];
  } catch {
    return [];
  }
}

export async function fetchCalendarAttendances(calendarId) {
  return [];
}

export async function punchAttendance(employeeId, checkedIn) {
  if (!employeeId) return;
  await post("/api/attendance/punch", {
    action: checkedIn ? "check_out" : "check_in",
  });
}

export function getWorkingDays(attendances) {
  if (attendances?.length) {
    return [...new Set(attendances.map((a) => Number(a.dayofweek)))];
  }
  return [1, 2, 3, 4, 5];
}

export function toDateStr(d) {
  if (typeof d === "string") d = new Date(d);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function getMonthDays(year, month) {
  const days = [];
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const pad = (first.getDay() + 6) % 7;
  for (let i = 0; i < pad; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
  return days;
}

export function getWeekDays(ref) {
  const d = new Date(ref);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d.setDate(diff));
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(mon);
    day.setDate(mon.getDate() + i);
    return day;
  });
}

function isGhost(a) {
  return (a.worked_hours || 0) < GHOST_THRESHOLD_H;
}

function sameDay(d1, d2) {
  return toDateStr(d1) === toDateStr(d2);
}

export function isLateTime(checkIn) {
  if (!checkIn) return false;
  const d = new Date(checkIn);
  return d.getHours() * 60 + d.getMinutes() > LATE_HOUR * 60 + LATE_MIN;
}

export function isEarlyTime(checkOut) {
  if (!checkOut) return false;
  const d = new Date(checkOut);
  return d.getHours() * 60 + d.getMinutes() < 17 * 60;
}

export async function buildAttendanceData(employeeId, dateFrom, dateTo, workingDays) {
  const [attRecords, leaves] = await Promise.all([
    fetchAttendance(employeeId, dateFrom, dateTo),
    fetchLeaves(employeeId),
  ]);

  const validRecords = attRecords.filter((a) => !isGhost(a));

  const leaveMap = {};
  for (const l of leaves) {
    if (!l.state || l.state === "APPROVED" || l.state === "approved" || l.state === "validate") {
      const from = new Date(l.date_from);
      const to = new Date(l.date_to);
      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        leaveMap[toDateStr(d)] = l;
      }
    }
  }

  const result = [];
  const start = new Date(dateFrom);
  const end = new Date(dateTo);

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = toDateStr(d);
    const dow = d.getDay();
    const odDay = dow === 0 ? 7 : dow;
    const isWork = workingDays.includes(odDay);
    const leave = leaveMap[key];

    const dayRecords = validRecords.filter((a) => {
      const ci = a.check_in ? toDateStr(a.check_in) : null;
      return ci === key;
    });

    if (dayRecords.length > 0) {
      const cis = dayRecords.map((a) => a.check_in).filter(Boolean).sort();
      const cos = dayRecords.map((a) => a.check_out).filter(Boolean).sort().reverse();
      const ci = cis[0] || null;
      const co = cos[0] || null;
      const totalHours = ci && co ? (new Date(co) - new Date(ci)) / 3600000 : null;
      const late = ci ? isLateTime(ci) : false;
      const early = co ? isEarlyTime(co) : false;
      const crossDay = ci && co && !sameDay(ci, co);

      result.push({
        date: new Date(d),
        dateStr: key,
        dow,
        checkIn: ci,
        checkOut: co,
        hours: totalHours,
        status: "present",
        isLate: late,
        isEarly: early && !crossDay,
        crossDay,
        ghostCount: attRecords.filter((a) => {
          const ak = a.check_in ? toDateStr(a.check_in) : null;
          return ak === key && isGhost(a);
        }).length,
      });
    } else if (leave) {
      result.push({ date: new Date(d), dateStr: key, dow, checkIn: null, checkOut: null, hours: null, status: "on_leave", leaveName: leave.name });
    } else if (isWork) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const isFuture = new Date(d) > today;
      result.push({ date: new Date(d), dateStr: key, dow, checkIn: null, checkOut: null, hours: null, status: isFuture ? "future" : "absent" });
    } else {
      result.push({ date: new Date(d), dateStr: key, dow, checkIn: null, checkOut: null, hours: null, status: "weekend" });
    }
  }

  return result;
}
